import type { DashboardAnnualDaySlot } from './dashboardAnnualDaySlots';

export type LessonMenuTitleSlot = Pick<
  DashboardAnnualDaySlot,
  'periodLabel' | 'templateLabel' | 'programKindLabel' | 'display'
>;

/** 쪽수(보조쪽수). 보조가 없으면 괄호 없이 쪽수만 — 데일리 수업 블럭과 같은 표기 */
function formatLessonPageParen(pageText: string, assistantPageText: string): string {
  const page = (pageText ?? '').trim();
  const asst = (assistantPageText ?? '').trim();
  if (page && asst) return `${page} (${asst})`;
  if (page) return page;
  if (asst) return `(${asst})`;
  return '';
}

/** 단계 머리줄(도입:, 배움1:, 정리: …) 또는 #활동/*활동 같은 계획 항목 줄 */
const LEARNING_PLAN_LINE_RE =
  /^\s*(?:(?:도입|배움\s*\d*|전개\s*\d*|정리|마무리)\s*[:：]|[#*＊]\s*(?:활동|내용|형태|기능|핵심\s*질문|평가\s*질문|확인\s*기준|피드백)(?:\s|$))/;

/**
 * 표시용: 학습내용 메모에서 단계별 학습계획(도입:/배움1:/정리: …) 부분을 뺀다.
 * 앞쪽 학습 목표 등은 남긴다. 저장된 값은 바꾸지 않는다.
 */
export function stripLearningPlanForDisplay(text: string): string {
  const lines = (text ?? '').split(/\r?\n/);
  const cut = lines.findIndex((line) => LEARNING_PLAN_LINE_RE.test(line));
  const kept = cut < 0 ? lines : lines.slice(0, cut);
  return kept.join('\n').trim();
}

export function lessonMenuTitleParts(
  slot: LessonMenuTitleSlot,
  options: { hideLearningPlan?: boolean } = {}
): {
  head: string;
  unit: string;
  page: string;
  learning: string;
} {
  const period = (slot.periodLabel ?? '').trim() || '수업';
  const pyeonje = (slot.templateLabel ?? '').trim();
  const d = slot.display;
  const unit = (d.progressUnit ?? '').trim();
  const note = (d.progressLearningNote ?? '').trim();
  const learning = [(d.progressLearning ?? '').trim(), options.hideLearningPlan ? stripLearningPlanForDisplay(note) : note]
    .filter(Boolean)
    .join(' ');
  if ((slot.programKindLabel ?? '').trim() === '고정') {
    return { head: period, unit, page: '', learning: '' };
  }
  return {
    head: pyeonje ? `${period} - ${pyeonje}` : period,
    unit,
    page: formatLessonPageParen(d.progressPage ?? '', d.progressAssistantPage ?? ''),
    learning,
  };
}

function formatGuidePageText(pageText: string, assistantPageText: string): string {
  const main = (pageText ?? '').trim();
  const assist = (assistantPageText ?? '').trim();
  if (main && assist) return `${main} / 보조 ${assist}`;
  if (main) return main;
  if (assist) return `보조 ${assist}`;
  return '';
}

function formatGuidePeriodFraction(currentPeriod: string, totalPeriod: string): string {
  const cur = (currentPeriod ?? '').trim();
  const tot = (totalPeriod ?? '').trim();
  if (!cur && !tot) return '';
  return `(${cur || '-'} / ${tot || '-'})`;
}

/** 주간학습 안내: 교시-편제 / 단원+쪽수 / 학습내용+(차시) / 준비물 */
export function weeklyGuideLessonMenuTitleParts(slot: LessonMenuTitleSlot): {
  head: string;
  unit: string;
  page: string;
  learning: string;
  periodFraction: string;
  material: string;
} {
  const base = lessonMenuTitleParts(slot);
  const d = slot.display;
  return {
    head: base.head,
    unit: base.unit,
    page: formatGuidePageText(d.progressPage ?? '', d.progressAssistantPage ?? ''),
    learning: base.learning,
    periodFraction: formatGuidePeriodFraction(d.progressCurrentPeriod ?? '', d.progressTotalPeriod ?? ''),
    material: (d.progressMaterial ?? '').trim(),
  };
}
